/* eslint-disable jsx-a11y/anchor-is-valid */
import { Button, Card, Label, TextInput } from "flowbite-react";
import type { FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/main";
import toast from "react-hot-toast";

const ForgotPasswordPage: FC = function () {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await authService.forgotPassword(email);
      setSubmitted(true);
      toast.success("Password reset instructions sent to your email!");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      toast.error(`Failed to send reset email: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center px-6 lg:h-screen lg:gap-y-12">
      <a href="/" className="my-6 flex items-center gap-x-1 lg:my-0">
        <img
          alt="CRM Platform logo"
          src="https://stage.vidzai.com/ops/images/logo.png"
          className="mr-3 h-10"
        />
        <span className="self-center whitespace-nowrap text-2xl font-semibold dark:text-white">
          CRM Platform
        </span>
      </a>
      <Card className="w-full lg:max-w-[640px] lg:[&>*]:w-full lg:[&>*]:p-16">
        <h1 className="text-2xl font-bold dark:text-white md:text-3xl">
          Forgot your password?
        </h1>
        {!submitted ? (
          <>
            <p className="mb-3 text-gray-500 dark:text-gray-300">
              Don't worry! Just enter your email address and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="mb-6 flex flex-col gap-y-3">
                <Label htmlFor="email">Your email</Label>
                <TextInput
                  id="email"
                  name="email"
                  placeholder="name@company.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="mb-6">
                <Button 
                  type="submit" 
                  className="w-full lg:w-auto"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send reset link"}
                </Button>
              </div>
            </form>
            <p className="text-sm text-gray-500 dark:text-gray-300">
              Remember your password?&nbsp;
              <button
                onClick={() => navigate("/authentication/sign-in")}
                className="text-primary-600 dark:text-primary-300 hover:underline"
              >
                Sign in
              </button>
            </p>
          </>
        ) : (
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="mb-3 text-gray-500 dark:text-gray-300">
              We've sent password reset instructions to <strong>{email}</strong>
            </p>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-300">
              Please check your email and click the link to reset your password. The link will expire in 1 hour.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate("/authentication/sign-in")}
                className="w-full lg:w-auto"
              >
                Back to sign in
              </Button>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setEmail("");
                }}
                className="text-sm text-primary-600 dark:text-primary-300 hover:underline"
              >
                Send another email
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
